import { mdiCloudUploadOutline, mdiReload } from '@mdi/js';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useRouteMatch } from 'react-router';
import { getCoreCover } from '../../apis/insuranceContext';
import { agreementDocumentPath } from '../../containers/thunkHelpers';
import { hasRole, hasAnyRole } from '../../containers/User';
import useDialog from '../../hooks/useDialog';
import { RoleCategoryEnum } from '../../lib';
import { getFormatedPeriod, mapBackendStatusToFrontend } from '../../lib/helpers';
import XHR_STATUS from '../../lib/xhrStatus';
import * as constants from '../constVariables';
import { ReactComponent as Loader } from '../Icons/loader.svg';
import DescriptionItem from '../Shared/DescriptionItem';
import IconButton from '../Shared/IconButton';
import ItsDialog, { DialogHeader } from '../Shared/ItsDialog';
import AddFiles from './AddFiles';
import styles from './CaseDocumentSearchBarAgreement.module.scss';

const CaseDocumentSearchBarAgreement = () => {
  const { caseType } = useParams();
  const { user } = useSelector((state) => ({ user: state.user }));
  const isComplianceOfficer = hasRole(user, RoleCategoryEnum.COMPLIANCE_OFFICER);
  const { isShowing, show, hide } = useDialog();
  const [currentContext, setCurrentContext] = useState(null);

  const setContext = (context) => {
    setCurrentContext(context);
  };

  return (
    <>
      <div className={styles.searchWrapper}>
        <div className={styles.contextWrapper}>{caseType === 'agreement' && <AgreementContext setContext={setContext} />}</div>
      </div>

      {currentContext && hasAnyRole(user) && !isComplianceOfficer && (
        <div className={styles.additionalActions}>
          <IconButton
            data-test-id="vQcEFdpe2VFOeQQvnPoLJ"
            icon={mdiCloudUploadOutline}
            iconSize={constants.SMALL_ICON_SIZE}
            onClick={show}
            text="Add a file"
          />
        </div>
      )}

      <ItsDialog isOpen={isShowing} onDismiss={hide} className={styles.addFilesDialog} aria-label="Add Document">
        <DialogHeader title="Add Document" close={hide} />

        {currentContext && <AddFiles currentContext={currentContext} onSubmit={hide} />}
      </ItsDialog>
    </>
  );
};

export default CaseDocumentSearchBarAgreement;

// eslint-disable-next-line react/prop-types
const AgreementContext = ({ setContext }) => {
  const { mainAgreementId, productAgreementId } = useRouteMatch(agreementDocumentPath).params;
  const getAgreement = useCallback(
    () => getCoreCover(productAgreementId === '0' ? mainAgreementId : productAgreementId),
    [mainAgreementId, productAgreementId]
  );

  const { getInsuranceContext, contentState, data, error } = useInsuranceContext(getAgreement);

  const retry = () => {
    getInsuranceContext();
  };

  useEffect(() => {
    setContext(data);
  }, [data, setContext]);

  switch (contentState) {
    case XHR_STATUS.ERROR:
      return (
        <div className={styles.error}>
          {error}
          <IconButton
            data-test-id="yllRyY_eaMSu4vksDJIun"
            className={styles.action}
            icon={mdiReload}
            text="Retry"
            iconSize={constants.SMALL_ICON_SIZE}
            onClick={retry}
          />
        </div>
      );
    case XHR_STATUS.SUCCESS:
      return (
        <dl className={styles.context}>
          <DescriptionItem className={styles.item} term="Policy Holder" detail={data?.peti} />
          <DescriptionItem className={styles.item} term="Policy Name" detail={data?.fleetName} />
          <DescriptionItem className={styles.item} term="Status" detail={mapBackendStatusToFrontend(data?.status)} />
          <DescriptionItem className={styles.item} term="Policy period" detail={getFormatedPeriod(data?.insurancePeriod)} />
          {data?.interest && <DescriptionItem className={styles.item} term="Product" detail={data.interest.name} />}
        </dl>
      );
    default:
      return (
        <div className={styles.loader}>
          <Loader />
        </div>
      );
  }
};

const useInsuranceContext = (fetchCallback) => {
  const [contentState, setContentState] = useState(XHR_STATUS.INITIAL);
  const [data, setData] = useState(null);
  const [error, setError] = useState();

  const getInsuranceContext = useCallback(async () => {
    setContentState(XHR_STATUS.LOADING);
    try {
      const result = await fetchCallback();
      setData(result);
      setContentState(XHR_STATUS.SUCCESS);
    } catch (axiosError) {
      setError('Unable to load insurance context');

      if (axiosError.response.status === 404) {
        setError('Insurance Context not found or does not contain any documents anymore');
      }

      setContentState(XHR_STATUS.ERROR);
    }
  }, [fetchCallback]);

  useEffect(() => {
    getInsuranceContext();
  }, [getInsuranceContext]);

  return { getInsuranceContext, contentState, data, error };
};
